import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminFetchBoards } from "../../core/axios/endpoints";

/**
 * Overzicht van alle borden: "Direct" plus één bord per partner. Een bord
 * ontstaat automatisch zodra een klant partner wordt (Klanten-pagina).
 */
const BoardsPage = () => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminFetchBoards({ includeArchived })
      .then((res) => !cancelled && setBoards(res?.data ?? res ?? []))
      .catch((err) => !cancelled && setError(err?.response?.data?.error ?? "Borden laden mislukt."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [includeArchived]);

  return (
    <div className="page-wrapper px-8 pt-20 pb-12">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
            <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
            <span className="mx-1">/</span>
            <span>Borden</span>
          </nav>
          <h1 className="text-2xl font-medium text-ink">Borden</h1>
          <p className="mt-1 text-sm text-ink-subtlest">
            Eén bord per klant met al zijn aanvragen (BTW en subsidie), plus &ldquo;Direct&rdquo; voor aanvragen zonder partner.
          </p>
        </div>
        <label className="flex h-8 cursor-pointer items-center gap-2 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
          Toon gearchiveerde borden
        </label>
      </div>

      {error ? <div className="mb-4 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">{error}</div> : null}

      {loading && boards.length === 0 ? (
        <div className="rounded bg-white p-10 text-center text-sm text-ink-subtlest shadow-card">Borden laden…</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {boards.map((b) => {
            const client = b.clientUserId;
            const initials = (b.key ?? "?").slice(0, 3);
            return (
              <Link
                key={b._id}
                to={`/panel/admin/borden/${b._id}`}
                className={`group flex flex-col rounded bg-white p-5 shadow-card transition hover:bg-sunken ${b.archived ? "opacity-60" : ""}`}
              >
                <div className="flex items-start gap-3">
                  {client?.partnerLogoUrl ? (
                    <img src={client.partnerLogoUrl} alt="" className="h-10 w-10 flex-shrink-0 rounded object-contain ring-1 ring-line" />
                  ) : (
                    <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded text-xs font-bold text-white ${b.kind === "direct" ? "bg-ink-subtle" : "bg-brand"}`}>
                      {initials}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-base font-semibold text-ink group-hover:text-brand">{b.name}</span>
                      <span className="text-xs font-medium text-ink-subtlest">{b.key}</span>
                    </div>
                    <div className="truncate text-xs text-ink-subtlest">
                      {b.kind === "direct"
                        ? "Zonder partner"
                        : client
                        ? [client.btwPartnerEnabled ? "BTW" : null, ...(client.partnerModules ?? []).map((m) => ({ eia: "EIA", eiaZon: "EIA Zon", isde: "ISDE" }[m] ?? m))].filter(Boolean).join(" · ") || "Partner"
                        : "Partner"}
                      {b.archived ? " · gearchiveerd" : ""}
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-line pt-3 text-center">
                  <div>
                    <div className="text-lg font-medium tabular-nums text-ink">{b.counts?.open ?? 0}</div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Open</div>
                  </div>
                  <div>
                    <div className="text-lg font-medium tabular-nums text-ink">
                      {b.counts?.btw ?? 0}<span className="text-xs font-normal text-ink-subtlest"> / {b.counts?.subsidie ?? 0}</span>
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">BTW / subsidie</div>
                  </div>
                  <div>
                    <div className={`text-lg font-medium tabular-nums ${b.counts?.toInvoice ? "text-[#5E4DB2]" : "text-ink"}`}>{b.counts?.toInvoice ?? 0}</div>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Te factureren</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BoardsPage;
